// Importing stores from a CSV file (the console's "Import" on Stores). Ports of CSVService.parseCSV / parseRecords
// (GroundTeam/Services/CSVService.swift), SettingsExport.readText (BOM), AppStore.prepareImportedRecord
// (Settings+Store.swift:503), AppStore.addRecord (AppStore.swift:227) and AppStore.duplicateKey (MapTab+Store.swift:240).
//
// - Parsing is the iOS one: same RFC-4180-ish reader (quoted fields with commas, quotes and line breaks), the same
//   header aliases (case-insensitive), the same type / priority / value / tags rules, name falling back to company,
//   company-only rows becoming accounts. Two differences: a CRLF line end is one line break here (iOS compares Swift
//   Characters, where "\r\n" is a single Character matching neither "\n" nor "\r", so a CRLF file reads as one long
//   row there), and a coordinate must be finite, on the globe and not (0, 0) (iOS takes any number; the map draws no
//   pin for these, so they are counted as "No coordinates").
// - The web also reads columns iOS ignores (iOS skips unknown headers, so files stay usable on both): website, external
//   id (`Record.externalId`, used to skip stores already imported) and lead source (matched by name).
// - What iOS does per row on import: prepareImportedRecord (owner = importer, source = the company source named
//   "Import", name = company, else street, else "Imported record"), then addRecord (pipeline = the type's default,
//   first stage, stage entered now, territory by the assignment rule, created by/at, and one "<Type> created" activity
//   per record). iOS does not skip duplicates when importing; the console does (name + postal code as when adding
//   prospects, and external id), and leaves out rows with no name, company or street instead of naming them
//   "Imported record". Two places that both carry an external id are told apart by it alone: a chain's two shops with
//   the same name in one ZIP are two stores when their ids differ.
//
// Everything here is pure and linear in the number of rows (Sets and Maps, no pairwise comparison), so a 5,000-row
// file plans in a few milliseconds.

import { PRIORITIES, RECORD_TYPES, RECORD_TYPE_TITLES, type Priority, type RecordType } from '@/contracts/enums';
import {
  newActivity,
  newRecord,
  recordDisplayName,
  type AddressPayload,
  type CoordinatePayload,
  type LeadSourcePayload,
  type PipelinePayload,
  type RecordPayload,
  type TerritoryPayload,
} from '@/contracts/models';
import type { JsonObject } from '@/contracts/wire';
import { toWireDate } from '@/contracts/wireDate';
import { patch, type Patch } from '@/sync/patch';
import { isValidCoordinate } from './geo';
import { orderedStages, pipelineFor } from './pipelines';
import { territoryForRecord } from './territories';
import { trimAll, trimSpaces } from './util';

// MARK: - CSV reader

/**
 * `CSVService.parseCSV`: rows of fields. A quote opens a quoted run anywhere in a field; inside it `""` is a quote and
 * commas and line breaks are kept. Line breaks are LF, CR or CRLF. A leading UTF-8 BOM is dropped (readText).
 */
export function parseCsv(text: string): string[][] {
  const rows: string[][] = [];
  let row: string[] = [];
  let field = '';
  let inQuotes = false;
  const n = text.length;
  let i = text.charCodeAt(0) === 0xfeff ? 1 : 0;
  while (i < n) {
    if (inQuotes) {
      const q = text.indexOf('"', i);
      if (q === -1) {
        // An unclosed quote takes the rest of the file, as on iOS.
        field += text.slice(i);
        break;
      }
      field += text.slice(i, q);
      if (text.charCodeAt(q + 1) === 34) {
        field += '"';
        i = q + 2;
      } else {
        inQuotes = false;
        i = q + 1;
      }
      continue;
    }
    const c = text.charCodeAt(i);
    if (c === 34) {
      inQuotes = true;
      i++;
    } else if (c === 44) {
      row.push(field);
      field = '';
      i++;
    } else if (c === 10 || c === 13) {
      i += c === 13 && text.charCodeAt(i + 1) === 10 ? 2 : 1;
      row.push(field);
      field = '';
      rows.push(row);
      row = [];
    } else {
      // A run of plain characters at once (the common case), not one concatenation per character.
      let j = i + 1;
      while (j < n) {
        const d = text.charCodeAt(j);
        if (d === 34 || d === 44 || d === 10 || d === 13) break;
        j++;
      }
      field += text.slice(i, j);
      i = j;
    }
  }
  if (field !== '' || row.length > 0) {
    row.push(field);
    rows.push(row);
  }
  return rows;
}

// MARK: - Columns

/** The record fields a column can fill. The last three are read by the web only. */
export const IMPORT_FIELDS = [
  'name',
  'company',
  'street',
  'city',
  'state',
  'postalCode',
  'latitude',
  'longitude',
  'phone',
  'email',
  'type',
  'priority',
  'value',
  'notes',
  'tags',
  'website',
  'externalId',
  'source',
] as const;
export type ImportField = (typeof IMPORT_FIELDS)[number];

/** Header names per field (lower-case; `parseRecords`' lists, then the web's own). The first matching column wins. */
export const IMPORT_HEADER_ALIASES: Readonly<Record<ImportField, readonly string[]>> = {
  name: ['name', 'full name', 'lead', 'record'],
  company: ['company', 'business', 'account'],
  street: ['street', 'address', 'address 1', 'street address'],
  city: ['city'],
  state: ['state', 'province'],
  postalCode: ['zip', 'zip code', 'postal code', 'postcode'],
  latitude: ['latitude', 'lat'],
  longitude: ['longitude', 'lon', 'lng'],
  phone: ['phone', 'phone number', 'mobile'],
  email: ['email', 'e-mail'],
  type: ['type', 'record type'],
  priority: ['priority'],
  value: ['value', 'deal value', 'amount'],
  notes: ['notes', 'note'],
  tags: ['tags'],
  website: ['website', 'url', 'web site'],
  externalId: ['external id', 'externalid', 'source id'],
  source: ['lead source', 'source'],
};

/** Column index per field (missing when the file has no such column). */
export type ImportColumns = Partial<Record<ImportField, number>>;

/** `idx(_:)` for every field: the first column whose trimmed, lower-cased header is one of the field's names. */
export function importColumns(header: readonly string[]): ImportColumns {
  const cols = header.map((h) => trimSpaces(h.toLowerCase()));
  const out: ImportColumns = {};
  for (const f of IMPORT_FIELDS) {
    const names = IMPORT_HEADER_ALIASES[f];
    const i = cols.findIndex((c) => names.includes(c));
    if (i !== -1) out[f] = i;
  }
  return out;
}

// MARK: - Rows

/** One data row as iOS reads it, plus the web-only columns. Values are kept as written unless iOS trims them. */
export interface ImportRow {
  /** 1 for the first row under the header (blank rows count, so it matches the file's order). */
  rowNumber: number;
  type: RecordType;
  /** The type came from a type column value (not the company-only / lead default). */
  typeFromFile: boolean;
  name: string;
  company: string;
  street: string;
  city: string;
  state: string;
  postalCode: string;
  coordinate?: CoordinatePayload;
  phone: string;
  email: string;
  website: string;
  priority: Priority;
  value: number;
  notes: string;
  tags: string[];
  externalId: string;
  /** Lead source name from a source column ('' without one). */
  sourceName: string;
}

export interface ParsedImport {
  /** Header cells as written (trimmed), in file order. */
  header: string[];
  columns: ImportColumns;
  /** Data rows with at least one non-empty field. */
  rows: ImportRow[];
}

// Swift `Double(String)`: optional sign, digits with an optional fraction and exponent, nothing around it.
const DECIMAL = /^[+-]?(?:\d+\.?\d*|\.\d+)(?:[eE][+-]?\d+)?$/;
const UNSIGNED_DECIMAL = /^(?:\d+\.?\d*|\.\d+)$/;

/** `Double(text)`, or undefined when Swift would return nil (or the number is not finite). */
export function swiftDouble(text: string): number | undefined {
  if (!DECIMAL.test(text)) return undefined;
  const v = Number(text);
  return Number.isFinite(v) ? v : undefined;
}

/**
 * iOS takes the pair when both parse; the console also needs one the map can draw (`isValidCoordinate`: on the globe
 * and not the (0, 0) placeholder of an address that was never geocoded), else the row counts as without coordinates.
 */
export function importCoordinate(lat: string, lon: string): CoordinatePayload | undefined {
  const latitude = swiftDouble(lat);
  const longitude = swiftDouble(lon);
  if (latitude === undefined || longitude === undefined) return undefined;
  const coordinate = { latitude, longitude };
  return isValidCoordinate(coordinate) ? coordinate : undefined;
}

/** `Double(value.filter { "0123456789.".contains($0) }) ?? 0`: "$1,250.50" is 1250.5, "-5" is 5. */
export function importValue(text: string): number {
  const digits = text.replace(/[^0-9.]/g, '');
  return UNSIGNED_DECIMAL.test(digits) ? Number(digits) : 0;
}

/** `split(whereSeparator: ; or |)`, each trimmed, empties dropped. */
export function importTags(text: string): string[] {
  return text
    .split(/[;|]/)
    .map(trimSpaces)
    .filter((t) => t !== '');
}

/** `CSVService.parseRecords` (the iOS rules per field) over an already split table. */
export function readImportRows(table: readonly (readonly string[])[]): ParsedImport {
  const headerRow = table[0];
  if (!headerRow) return { header: [], columns: {}, rows: [] };
  const columns = importColumns(headerRow);
  const rows: ImportRow[] = [];
  for (let r = 1; r < table.length; r++) {
    const row = table[r]!;
    if (!row.some((f) => f !== '')) continue;
    const v = (f: ImportField): string => {
      const i = columns[f];
      return i !== undefined && i < row.length ? row[i]! : '';
    };
    const rawName = trimSpaces(v('name'));
    const rawCompany = trimSpaces(v('company'));
    const typed = trimSpaces(v('type').toLowerCase());
    const typeFromFile = (RECORD_TYPES as readonly string[]).includes(typed);
    const type: RecordType = typeFromFile ? (typed as RecordType) : rawName === '' && rawCompany !== '' ? 'account' : 'lead';
    const upper = v('priority').toUpperCase();
    const priority: Priority = (PRIORITIES as readonly string[]).includes(upper) ? (upper as Priority) : 'C';
    const coordinate = importCoordinate(v('latitude'), v('longitude'));
    rows.push({
      rowNumber: r,
      type,
      typeFromFile,
      name: rawName !== '' ? rawName : rawCompany,
      company: rawCompany,
      street: v('street'),
      city: v('city'),
      state: v('state'),
      postalCode: v('postalCode'),
      ...(coordinate ? { coordinate } : {}),
      phone: v('phone'),
      email: v('email'),
      website: trimAll(v('website')),
      priority,
      value: importValue(v('value')),
      notes: v('notes'),
      tags: importTags(v('tags')),
      externalId: trimAll(v('externalId')),
      sourceName: trimAll(v('source')),
    });
  }
  return { header: headerRow.map(trimSpaces), columns, rows };
}

/** The file's text → header, columns and rows (BOM, CRLF and quoted line breaks handled). */
export function parseImportCsv(text: string): ParsedImport {
  return readImportRows(parseCsv(text));
}

// MARK: - Duplicates and the plan

/**
 * `AppStore.duplicateKey`: lower-cased letters and digits of the name, "|", the same of the postal code. NFC first,
 * because Swift compares strings by canonical equivalence ("é" typed either way is one key there).
 */
export function duplicateKey(name: string, postalCode: string): string {
  const norm = (s: string) => s.normalize('NFC').toLowerCase().replace(/[^\p{L}\p{M}\p{N}]/gu, '');
  return `${norm(name)}|${norm(postalCode)}`;
}

/** `prepareImportedRecord`'s name: the name (already the company when empty), else the street. '' = no name at all. */
export function importName(row: Pick<ImportRow, 'name' | 'street'>): string {
  return row.name !== '' ? row.name : row.street;
}

/** The key a row will have once imported (its display name and postal code). */
export function importRowKey(row: Pick<ImportRow, 'name' | 'company' | 'street' | 'postalCode'>): string {
  return duplicateKey(recordDisplayName({ name: importName(row), company: row.company }), row.postalCode);
}

export interface ImportPlan {
  /** Data rows in the file. */
  total: number;
  /** Rows that will become stores, in file order. */
  toAdd: ImportRow[];
  /**
   * Same external id, or same name and postal code (unless both have external ids and they differ), as a store
   * already in the company (archived ones included).
   */
  skippedExisting: number;
  /** The same match against an earlier row of the file. */
  skippedRepeated: number;
  /** No name, company or street. */
  skippedNoName: number;
  /** Rows to add without a usable latitude/longitude: imported, but not on the map. */
  withoutCoordinates: number;
}

/**
 * The stores seen so far, for duplicate checks. Same external id is always the same place. Same name and postal code
 * is the same place unless both sides carry an external id (then the ids decide), so `keys` holds every key and
 * `keysWithoutExternal` the keys of stores without an id.
 */
class SeenStores {
  private readonly keys = new Set<string>();
  private readonly keysWithoutExternal = new Set<string>();
  private readonly external = new Set<string>();

  add(key: string, ext: string): void {
    this.keys.add(key);
    if (ext === '') this.keysWithoutExternal.add(key);
    else this.external.add(ext);
  }

  has(key: string, ext: string): boolean {
    if (ext === '') return this.keys.has(key);
    return this.external.has(ext) || this.keysWithoutExternal.has(key);
  }
}

/**
 * Which rows to add. `existing` is every store the replica has (archived included, as `existingDuplicateKeys`); a
 * row matching one by external id, or by name + postal code where one side has no external id (`SeenStores`), is
 * skipped, and so is a row matching an earlier row of the file the same way.
 */
export function planImport(
  rows: readonly ImportRow[],
  existing: readonly Pick<RecordPayload, 'name' | 'company' | 'address' | 'externalId'>[],
): ImportPlan {
  const inCompany = new SeenStores();
  for (const r of existing) inCompany.add(duplicateKey(recordDisplayName(r), r.address?.postalCode ?? ''), trimAll(r.externalId ?? ''));
  const inFile = new SeenStores();
  const plan: ImportPlan = { total: rows.length, toAdd: [], skippedExisting: 0, skippedRepeated: 0, skippedNoName: 0, withoutCoordinates: 0 };
  for (const row of rows) {
    if (importName(row) === '') {
      plan.skippedNoName++;
      continue;
    }
    const key = importRowKey(row);
    const ext = row.externalId;
    if (inCompany.has(key, ext)) {
      plan.skippedExisting++;
      continue;
    }
    if (inFile.has(key, ext)) {
      plan.skippedRepeated++;
      continue;
    }
    inFile.add(key, ext);
    plan.toAdd.push(row);
    if (!row.coordinate) plan.withoutCoordinates++;
  }
  return plan;
}

/** Rows per record type once `typeOverride` (null: as in the file) is applied. */
export function importTypeCounts(rows: readonly Pick<ImportRow, 'type'>[], typeOverride: RecordType | null): Record<RecordType, number> {
  const counts: Record<RecordType, number> = { lead: 0, account: 0, contact: 0 };
  for (const r of rows) counts[typeOverride ?? r.type]++;
  return counts;
}

// MARK: - Lead sources

const sourceKey = (name: string) => trimAll(name).toLowerCase();

/** `prepareImportedRecord`'s default: the company source named "Import" (any case), if there is one. */
export function defaultImportSource(sources: readonly LeadSourcePayload[]): LeadSourcePayload | undefined {
  return sources.find((s) => sourceKey(s.name) === 'import');
}

/** Source names in the file that match no company source (their rows get the default source). */
export function unknownSourceNames(rows: readonly Pick<ImportRow, 'sourceName'>[], sources: readonly LeadSourcePayload[]): string[] {
  const known = new Set(sources.map((s) => sourceKey(s.name)));
  const seen = new Map<string, string>();
  for (const r of rows) {
    const k = sourceKey(r.sourceName);
    if (k !== '' && !known.has(k) && !seen.has(k)) seen.set(k, trimAll(r.sourceName));
  }
  return [...seen.values()];
}

// MARK: - Payloads

export interface ImportOptions {
  /** Owner of every imported store; null leaves them unassigned. */
  ownerId: string | null;
  /** Lead source for rows whose own source column is empty or unknown; null for none. */
  defaultSourceId: string | null;
  /** Every row gets this type; null keeps each row's own (type column, else account for company-only, else lead). */
  typeOverride: RecordType | null;
  /** The importer's profile (`createdBy`, and the user of the "created" activities). */
  createdBy: string | null;
  sources: readonly LeadSourcePayload[];
  pipelines: readonly PipelinePayload[];
  territories: readonly TerritoryPayload[];
  /** One "<Type> created" activity per store, as iOS `addRecord` logs. */
  logCreation: boolean;
  now: Date;
}

const asJson = (v: object): JsonObject => v as unknown as JsonObject;

/**
 * Builds the patches for rows, `addRecord` style: a `records` create per row (every key from `newRecord`, upper-case
 * ids, whole-second dates) and, with `logCreation`, its "created" activity right after it. Lookups are prepared once.
 */
export function importPatchBuilder(options: ImportOptions): (rows: readonly ImportRow[]) => Patch[] {
  const at = toWireDate(options.now);
  const sourceByName = new Map<string, string>();
  for (const s of options.sources) {
    const k = sourceKey(s.name);
    if (k !== '' && !sourceByName.has(k)) sourceByName.set(k, s.id);
  }
  const placement = new Map<RecordType, { pipelineId?: string; stageId?: string }>();
  const placeFor = (type: RecordType) => {
    let p = placement.get(type);
    if (!p) {
      const pipeline = pipelineFor({ type }, options.pipelines);
      const stage = pipeline ? orderedStages(pipeline)[0] : undefined;
      p = { ...(pipeline ? { pipelineId: pipeline.id } : {}), ...(stage ? { stageId: stage.id } : {}) };
      placement.set(type, p);
    }
    return p;
  };

  return (rows) => {
    const out: Patch[] = [];
    for (const row of rows) {
      const type = options.typeOverride ?? row.type;
      const address: AddressPayload = {
        street: row.street,
        unit: '',
        city: row.city,
        state: row.state,
        postalCode: row.postalCode,
        country: 'US',
      };
      const territory = territoryForRecord({ coordinate: row.coordinate, address }, options.territories);
      const sourceId = sourceByName.get(sourceKey(row.sourceName)) ?? options.defaultSourceId ?? undefined;
      const record = newRecord({
        type,
        name: importName(row),
        company: row.company,
        address,
        ...(row.coordinate ? { coordinate: { latitude: row.coordinate.latitude, longitude: row.coordinate.longitude } } : {}),
        phone: row.phone,
        email: row.email,
        website: row.website,
        notes: row.notes,
        ...placeFor(type),
        priority: row.priority,
        value: row.value,
        ...(options.ownerId ? { ownerId: options.ownerId } : {}),
        ...(territory ? { territoryId: territory.id } : {}),
        ...(sourceId ? { sourceId } : {}),
        tags: [...row.tags],
        externalId: row.externalId,
        stageEnteredAt: at,
        ...(options.createdBy ? { createdBy: options.createdBy } : {}),
        createdAt: at,
      });
      out.push(patch.create('records', record.id, asJson(record)));
      if (options.logCreation) {
        const activity = newActivity({
          recordId: record.id,
          type: 'created',
          title: `${RECORD_TYPE_TITLES[type]} created`,
          ...(options.createdBy ? { userId: options.createdBy } : {}),
          createdAt: at,
          completedAt: at,
        });
        out.push(patch.create('activities', activity.id, asJson(activity)));
      }
    }
    return out;
  };
}

/** Splits rows into save batches of `size` (the last one shorter). */
export function importChunks<T>(items: readonly T[], size: number): T[][] {
  const step = Math.max(1, Math.floor(size));
  const out: T[][] = [];
  for (let i = 0; i < items.length; i += step) out.push(items.slice(i, i + step));
  return out;
}
